import { useMemo } from "react";
import { usePrefetchedStageList } from "@/features/order-instructions/hooks/use-stage-list";
import { NO_STAMP } from "@/features/order-instructions/lib/format-stamp";
import { type OrderRole, useOrderRole } from "@/features/order-instructions/lib/order-role";
import {
  type OrderRow,
  rowsInTab,
  unreadInView,
} from "@/features/order-instructions/lib/order-stages";
import { bookOf } from "@/features/order-instructions/lib/order-view";
import type { StageList } from "@/features/order-instructions/lib/stage-list";

export type WaitingKind = "to-confirm" | "signed-order";

export interface WaitingItem {
  key: string;
  kind: WaitingKind;
  clientId: string | null;
  clientName: string;
  portfolioName: string | null;
}

export interface WaitingItems {
  items: WaitingItem[];
  done: boolean;
  unread: number;
  failed: boolean;
}

export interface WaitingGroup {
  kind: WaitingKind;
  items: WaitingItem[];
  clientIds: string[];
}

export function useWaitingItems(enabled: boolean): WaitingItems | null {
  const list = usePrefetchedStageList();
  const role = useOrderRole();
  return useMemo(
    () => (!enabled || role === null ? null : waitingOf(list, role)),
    [enabled, list, role],
  );
}

export function waitingOf(list: StageList, role: OrderRole): WaitingItems {
  const book = bookOf(list, role);
  const items = WAITING_ORDER.flatMap((kind) =>
    rowsInTab(list.rows, TAB_OF_KIND[kind], book).map((row) => waitingItem(row, kind)),
  );
  return {
    items,
    done: list.state !== "loading",
    unread: unreadInView(list.unread, book).length + (list.instructionsFailed ? 1 : 0),
    failed: list.state === "error",
  };
}

export function groupWaiting(items: readonly WaitingItem[]): WaitingGroup[] {
  return WAITING_ORDER.flatMap((kind): WaitingGroup[] => {
    const grouped = items.filter((item) => item.kind === kind);
    if (grouped.length === 0) return [];
    const clientIds = [...new Set(grouped.map((item) => item.clientId ?? item.key))];
    return [{ kind, items: grouped, clientIds }];
  });
}

function waitingItem(row: OrderRow, kind: WaitingKind): WaitingItem {
  return {
    key: row.id,
    kind,
    clientId: row.subjectId,
    clientName: row.clientName ?? NO_STAMP,
    portfolioName: row.portfolioName,
  };
}

const WAITING_ORDER: readonly WaitingKind[] = ["to-confirm", "signed-order"];

const TAB_OF_KIND = { "to-confirm": "exec", "signed-order": "signed" } as const;
